import { Bot, BriefcaseBusiness } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { lacksFeature } from '@/modules/billing/workspacePlan';
import {
  type ActivityStep,
  getJobActivity,
  type JobActivity,
} from '@/modules/hiring-manager/utils/jobActivity';
import { outreachHold } from '@/modules/hiring-manager/utils/outreachReadiness';
import type { JobApi } from '@/modules/jobs/types';
import {
  Tooltip,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import {
  automationFromJob,
  customChoice,
  type JobAutomation,
  limitToPlan,
  matchingSetup,
} from '@/pages/hm/job-automation/jobAutomation';

import { JobStepsContent } from './JobStepsContent';
import { PortalEmptyState } from './portalEmptyState/PortalEmptyState';
import {
  PortalList,
  PortalListLimit,
  PortalListRow,
  PortalListSkeleton,
} from './PortalList';

interface HMActiveSearchesProps {
  /** False when the account has no jobs at all, not even a draft. */
  hasAnyJobs: boolean;
  isLoading: boolean;
  jobs: JobApi[];
  searchSuffix: string;
}

export function HMActiveSearches({
  hasAnyJobs,
  isLoading,
  jobs,
  searchSuffix,
}: HMActiveSearchesProps) {
  // Rows animate in only when they replace a skeleton, not when a cached
  // list renders on route change.
  const [mountedWhileLoading] = useState(isLoading);
  const workspacePlan = useWorkspacePlan();
  const { linkedInStatus } = useIsLinkedInConnected();
  // Managed leaves modes and outreach to its Finlay recruiter; self-service
  // plans send from the user's LinkedIn.
  const isSelfService =
    workspacePlan.plan === 'source' || workspacePlan.plan === 'free';
  // A plan without automation has a single mode, so there is nothing to name.
  const showsMode = isSelfService && !lacksFeature(workspacePlan, 'automation');

  if (isLoading) {
    return <PortalListSkeleton rows={3} />;
  }

  if (jobs.length === 0) {
    // The dashboard's first-run checklist carries the create CTA when the
    // account has nothing at all, so this stays a quiet line either way.
    return hasAnyJobs ? (
      <PortalEmptyState
        description="Jobs appear here once they are live."
        icon={BriefcaseBusiness}
        size="row"
        title="No active jobs"
      />
    ) : (
      <PortalEmptyState
        description="Active jobs and their status appear here."
        icon={BriefcaseBusiness}
        size="row"
        title="No jobs yet"
      />
    );
  }

  return (
    <PortalListLimit items={jobs}>
      {(visible) => (
        <PortalList animateEnter={mountedWhileLoading}>
          {visible.map((job, index) => {
            // The job's flags can ask for steps the plan does not include.
            const automation = limitToPlan(
              automationFromJob(job),
              workspacePlan,
            );
            const activity = getJobActivity({
              job,
              automation,
              // Managed outreach never waits for the user's LinkedIn.
              outreachHold: isSelfService
                ? outreachHold(job, linkedInStatus)
                : null,
            });
            return (
              <JobRow
                activity={activity}
                automation={automation}
                index={index + 1}
                isSelfService={isSelfService}
                job={job}
                key={job.id}
                searchSuffix={searchSuffix}
                showsMode={showsMode}
              />
            );
          })}
        </PortalList>
      )}
    </PortalListLimit>
  );
}

interface JobRowProps {
  activity: JobActivity;
  automation: JobAutomation;
  index: number;
  /** Lists the steps and who handles them; managed customers set none of it. */
  isSelfService: boolean;
  job: JobApi;
  searchSuffix: string;
  showsMode: boolean;
}

function JobRow({
  activity,
  automation,
  index,
  isSelfService,
  job,
  searchSuffix,
  showsMode,
}: JobRowProps) {
  // The status cannot take focus inside the row link, so keyboard focus on
  // the row opens its steps too. A tap opens the job, whose page lists them.
  const [isHovered, setIsHovered] = useState(false);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
  const stepsId = useId();
  const showsSteps = isSelfService && activity.summary !== null;
  const isStepsOpen = showsSteps && (isHovered || isKeyboardFocused);

  return (
    <PortalListRow
      aria-describedby={isStepsOpen ? stepsId : undefined}
      // One line from `md`; below it the status drops under the title,
      // so the chevron is centred on the first line only.
      className="grid-cols-[28px_minmax(0,1fr)_16px] gap-x-3.5 gap-y-1 md:grid-cols-[28px_minmax(0,1fr)_104px_190px_16px] lg:grid-cols-[28px_minmax(0,1fr)_96px_104px_190px_16px] [&>svg:last-child]:self-center"
      index={index}
      onBlur={() => setIsKeyboardFocused(false)}
      onFocus={(event) =>
        setIsKeyboardFocused(event.currentTarget.matches(':focus-visible'))
      }
      to={`/hm/jobs/${job.id}/candidates${searchSuffix}`}
    >
      <span className="bg-muted grid size-7 place-items-center rounded-[7px] text-neutral-500">
        <BriefcaseBusiness className="size-3.5" />
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-medium">{job.title}</span>
        {showsMode && <ModeChip automation={automation} />}
      </span>
      <span className="hidden truncate text-[13px] text-neutral-500 lg:block">
        {job.location}
      </span>
      <span className="hidden text-right text-[13px] text-neutral-500 tabular-nums md:block">
        {job.candidateCount != null && `${job.candidateCount} in pipeline`}
      </span>
      {activity.summary && (
        <span className="col-start-2 row-start-2 min-w-0 md:col-auto md:row-auto">
          {showsSteps ? (
            <StepsTooltip
              id={stepsId}
              jobTitle={job.title}
              onOpenChange={(open) => {
                setIsHovered(open);
                // Escape closes it for the keyboard as well.
                if (!open) setIsKeyboardFocused(false);
              }}
              open={isStepsOpen}
              steps={activity.steps}
            >
              <Summary summary={activity.summary} />
            </StepsTooltip>
          ) : (
            <Summary summary={activity.summary} />
          )}
        </span>
      )}
    </PortalListRow>
  );
}

/** The job's automation setup, as set on its Automation tab. */
function ModeChip({ automation }: { automation: JobAutomation }) {
  const setup = matchingSetup(automation);
  const isAutopilot = setup?.key === 'handsOff';
  return (
    <span
      className={cn(
        'text-primary inline-flex h-[18px] shrink-0 items-center gap-1 rounded-full text-[11px] font-medium whitespace-nowrap',
        isAutopilot ? 'bg-secondary-150 pr-[7px] pl-[5px]' : 'px-0.5',
      )}
    >
      <Bot className="size-[11px]" />
      {setup?.title ?? customChoice.title}
    </span>
  );
}

const summaryTone = {
  working: { dot: 'bg-primary', text: 'text-foreground' },
  attention: { dot: 'bg-amber-500', text: 'font-medium text-amber-700' },
  idle: { dot: 'bg-neutral-400', text: 'text-neutral-500' },
};

/** What Finlay is doing on the job, in a few words. */
function Summary({
  summary,
}: {
  summary: NonNullable<JobActivity['summary']>;
}) {
  const tone = summaryTone[summary.tone];
  return (
    <span className="flex min-w-0 items-center gap-2 text-xs">
      <span className="relative grid size-3 shrink-0 place-items-center">
        {summary.tone === 'working' && (
          <span className="bg-secondary-150 absolute inset-0 animate-[portal-live_1.6s_ease-in-out_infinite] rounded-full motion-reduce:animate-none" />
        )}
        <span className={cn('relative size-1.5 rounded-full', tone.dot)} />
      </span>
      <span className={cn('truncate', tone.text)}>{summary.text}</span>
    </span>
  );
}

/**
 * Every step of the job, opened by its row: on hover of the status, or
 * while the row has keyboard focus.
 */
function StepsTooltip({
  children,
  id,
  jobTitle,
  onOpenChange,
  open,
  steps,
}: {
  children: ReactNode;
  /** Describes the row link while open. */
  id: string;
  jobTitle: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  steps: ActivityStep[];
}) {
  return (
    <Tooltip onOpenChange={onOpenChange} open={open}>
      <TooltipTrigger asChild>
        <span className="inline-flex max-w-full">{children}</span>
      </TooltipTrigger>
      <JobStepsContent
        id={id}
        steps={steps}
        title={`What Finlay is doing on ${jobTitle}`}
      />
    </Tooltip>
  );
}
